import { useRef, useState, useEffect, useId } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { setBodyScrollLock } from '@/presentation/utils/bodyScrollLock';

/** Owner-id prefix for this bar's claim on the shared body scroll lock. */
const BODY_SCROLL_LOCK_OWNER_PREFIX = 'expandableTopBar';

interface ExpandableTopBarProps {
  collapsedHeight: string;
  isExpanded: boolean;
  onExpandedChange: (value: boolean) => void;
  backdropColor: string;
  header: ReactNode;
  body: ReactNode;
  collapseOnRoutePush?: boolean;
}

/**
 * A fixed-position top bar that:
 * - Matches the width/position of its parent container (not full viewport)
 * - Animates between collapsed (header only) and expanded (header + body) states
 * - Keeps the header visible at viewport top regardless of scroll position
 * - Locks page scroll while expanded
 * - Optionally collapses when the route changes (collapseOnRoutePush)
 */
export const ExpandableTopBar = ({
  collapsedHeight,
  isExpanded,
  onExpandedChange,
  backdropColor,
  header,
  body,
  collapseOnRoutePush = false,
}: ExpandableTopBarProps) => {
  const { topBar } = useResponsiveDesign();
  const placeholderRef = useRef<HTMLDivElement>(null);
  const [overlayGeometry, setOverlayGeometry] = useState<{ left: number; width: number } | null>(null);
  const location = useLocation();
  const mountPathRef = useRef(location.pathname);
  // Per-instance owner id: `MainRouteContainer` (and therefore this bar) is
  // cached per persistent main-nav route by `RouteStack`, so the `/` and
  // `/inbox` instances are mounted at the same time and both re-run the effect
  // below on every route change. A shared owner id would let the hidden
  // instance's release drop the visible instance's lock.
  const instanceId = useId();
  const lockOwner = `${BODY_SCROLL_LOCK_OWNER_PREFIX}:${instanceId}`;

  // Track parent container geometry for correct fixed positioning
  useEffect(() => {
    const el = placeholderRef.current;
    if (!el) return;

    const update = () => {
      const rect = el.getBoundingClientRect();
      setOverlayGeometry({ left: rect.left, width: rect.width });
    };

    const observer = new ResizeObserver(update);
    observer.observe(el);
    window.addEventListener('resize', update);
    update();

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  // Lock page scroll while expanded and on the mount path.
  // Including location.pathname in deps means scroll is restored in the same
  // effect run as the navigation — no render-cycle gap.
  //
  // Routed through the shared owner-keyed lock (`bodyScrollLock`) instead of
  // writing `document.body.style` directly: `AppOverlayHost` and `ExpauseSheet`
  // drive the same globals, and this bar's unconditional restore used to drop
  // their still-required lock. The concrete escape: the account-suspended
  // overlay takes its lock while still on `/login`, then this bar mounted
  // collapsed on `/` and cleared it, leaving the feed scrolling and selectable
  // behind a terminal, un-dismissible overlay. `fullViewport` covers the
  // `<html>` overflow / overscroll-behavior / scrollbar-gutter half this bar
  // additionally owns — reference-counted the same way.
  useEffect(() => {
    const onMountPath = location.pathname === mountPathRef.current;
    const shouldLock = isExpanded && onMountPath;

    setBodyScrollLock(lockOwner, shouldLock, { fullViewport: true });

    return () => {
      setBodyScrollLock(lockOwner, false);
    };
  }, [isExpanded, location.pathname, lockOwner]);

  // Collapse on route change when opted in
  useEffect(() => {
    if (collapseOnRoutePush) onExpandedChange(false);
  }, [location.pathname, collapseOnRoutePush, onExpandedChange]);

  const bodyHeight = `calc(100% - ${collapsedHeight})`;

  return (
    <>
      {/* Placeholder: reserves collapsedHeight in sticky flow, provides geometry source */}
      <div ref={placeholderRef} style={{ height: collapsedHeight }} />

      {/* Fixed overlay: positioned to match the placeholder's rendered width/left */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: overlayGeometry?.left ?? 0,
          width: overlayGeometry?.width ?? '100%',
          height: isExpanded ? '100dvh' : collapsedHeight,
          overflow: 'hidden',
          transition: `height ${ANIMATION_CONSTANTS.duration.slow}ms ${ANIMATION_CONSTANTS.easing.easeOut}`,
          zIndex: 60,
        }}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0"
          style={{
            backgroundColor: backdropColor,
            backdropFilter: `blur(${topBar.backdropBlur}px)`,
            WebkitBackdropFilter: `blur(${topBar.backdropBlur}px)`,
          }}
        />

        {/* Header — above backdrop, always at viewport top */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          {header}
        </div>

        {/* Body — fades in when expanded */}
        <div
          style={{
            position: 'relative',
            height: bodyHeight,
            overflowY: 'auto',
            opacity: isExpanded ? 1 : 0,
            transition: `
            opacity ${ANIMATION_CONSTANTS.duration.normal}ms 
            ${ANIMATION_CONSTANTS.easing.ease} ${isExpanded ?
                `${ANIMATION_CONSTANTS.duration.fast}ms` : '0s'}`,
            pointerEvents: isExpanded ? 'auto' : 'none',
          }}
        >
          {body}
        </div>
      </div>
    </>
  );
};
